import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Activity, ArrowLeft, X } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import usePageMeta from "@/hooks/usePageMeta";

export function TrackersIndex() {
  usePageMeta({
    title: "Industry Trackers — Live Intelligence · Luminary Insights & Advisory",
    description: "Real-time intelligence on capacity, capex, M&A, patents, regulation, and technology cycles across twelve high-growth industries.",
    path: "/trackers",
    keywords: "industry trackers, capex tracker, M&A tracker, patent tracker, market intelligence, decision intelligence",
  });
  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    api.get("/trackers").then((r) => setItems(r.data || []));
    api.get("/industries").then((r) => setIndustries(r.data || []));
  }, []);
  const filtered = filter ? items.filter((t) => t.industry_slug === filter) : items;
  return (
    <PublicLayout>
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-20" data-testid="trackers-index">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="eyebrow">Live Intelligence</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
            Industry Trackers.
          </h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--slate)] max-w-3xl">
            Real-time intelligence on capacity, capex, M&amp;A, patents, regulation, and technology — across twelve high-growth industries.
          </p>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="tracker-filters">
            <button
              onClick={() => setFilter("")}
              className={"font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border " + (!filter ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "border-[var(--border)] text-[var(--navy)] hover:border-[var(--navy)]")}
            >All</button>
            {industries.map((i) => (
              <button
                key={i.slug}
                onClick={() => setFilter(i.slug)}
                className={"font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors " + (filter === i.slug ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "border-[var(--border)] text-[var(--slate)] hover:border-[var(--navy)] hover:text-[var(--navy)]")}
              >{i.name}</button>
            ))}
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((t) => (
              <Link key={t.slug} to={`/trackers/${t.slug}`} className="group block bg-white border border-[var(--border)] p-6 hover-lift" data-testid={`tracker-card-${t.slug}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--gold)]">{t.tracker_kind} · {t.frequency}</div>
                  <Activity size={14} className="text-[var(--gold)]" />
                </div>
                <h3 className="font-display font-bold text-xl mt-3 text-[var(--navy)] group-hover:text-[var(--gold)] transition-colors leading-tight">{t.title}</h3>
                <p className="mt-2 text-sm text-[var(--slate)] leading-relaxed">{t.blurb}</p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {(t.metrics || []).slice(0, 4).map((m, i) => (
                    <div key={i} className="border-l-2 border-[var(--gold)] pl-3">
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)]">{m.label}</div>
                      <div className="font-display font-bold text-lg text-[var(--navy)]">{m.value}</div>
                      <div className="text-[10px] font-mono text-[var(--gold)]">{m.trend}</div>
                    </div>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export function TrackerDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/trackers/${slug}`).then((r) => setT(r.data)).catch(() => {}); }, [slug]);
  if (!t) return <PublicLayout><div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-sm">Loading…</div></PublicLayout>;
  return (
    <PublicLayout>
      <section className="border-b border-[var(--border)] bg-white py-12 sm:py-16" data-testid="tracker-detail">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to="/trackers" className="btn-ghost" data-testid="back-btn"><ArrowLeft size={12} /> All trackers</Link>
            <Link to="/" className="btn-ghost" data-testid="close-btn"><X size={12} /> Close</Link>
          </div>
          <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--gold)]">{t.tracker_kind} · {t.frequency} update</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,5.4vw,4.4rem)] mt-3 leading-[0.95] tracking-tight text-[var(--navy)]">{t.title}</h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--slate)]">{t.blurb}</p>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]" data-testid="tracker-metrics">
            {(t.metrics || []).map((m, i) => (
              <div key={i} className="bg-white p-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)]">{m.label}</div>
                <div className="font-display font-black text-3xl mt-2 text-[var(--navy)]">{m.value}</div>
                <div className="text-xs font-mono text-[var(--gold)] mt-1">{m.trend}</div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <div className="eyebrow">Signal highlights</div>
            <ul className="mt-5 space-y-4">
              {(t.highlights || []).map((h, i) => (
                <li key={i} className="border-l-2 border-[var(--gold)] pl-5 text-[17px] leading-relaxed text-[var(--navy)]">{h}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <section className="bg-[var(--navy)] text-white py-16">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 grid sm:grid-cols-2 gap-6 items-center">
          <div>
            <div className="eyebrow text-[var(--gold)]">Need deeper intelligence?</div>
            <h2 className="font-display font-bold text-3xl mt-3">Speak with Luminary.</h2>
          </div>
          <div className="flex flex-wrap gap-3 sm:justify-end">
            <Link to="/strategy-session" className="btn-gold">Schedule a Strategy Session <ArrowRight size={14} /></Link>
            <Link to="/luminous" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] hover:text-white inline-flex items-center gap-2 self-center">Or try Luminous AI →</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
